import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authService } from '@/services/auth.service';

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isTokenError, setIsTokenError] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Auto-redirect after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  async function onSubmit(data: FormData) {
    if (!token) {
      setIsTokenError(true);
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      await authService.resetPassword({ token, password: data.password });
      setSuccess(true);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: { message?: string; code?: string } } } };
      const errCode = axiosError.response?.data?.error?.code;
      if (errCode === 'INVALID_TOKEN' || errCode === 'TOKEN_EXPIRED') {
        setIsTokenError(true);
      } else {
        setError(axiosError.response?.data?.error?.message || 'Password reset failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <Card className="border-0 bg-white/80 shadow-lg shadow-purple-500/5 backdrop-blur-sm dark:bg-card/80 rounded-xl">
          <CardContent className="flex flex-col items-center p-6 sm:p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Password reset successfully</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Redirecting you to login...
            </p>
            <Link
              to="/login"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:text-brand-primary/80 hover:underline"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to login
            </Link>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Invalid/expired token state
  if (isTokenError || !token) {
    return (
      <AuthLayout>
        <Card className="border-0 bg-white/80 shadow-lg shadow-purple-500/5 backdrop-blur-sm dark:bg-card/80 rounded-xl">
          <CardContent className="flex flex-col items-center p-6 sm:p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Invalid or expired link</h2>
            <p className="mt-2 max-w-xs text-sm text-muted-foreground">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="mt-4 inline-flex items-center justify-center h-10 rounded-md bg-gradient-to-r from-brand-primary to-purple-600 px-6 text-sm font-medium text-white shadow-md hover:shadow-lg hover:opacity-90 transition-all"
            >
              Request new link
            </Link>
            <Link
              to="/login"
              className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to login
            </Link>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="border-0 bg-white/80 shadow-lg shadow-purple-500/5 backdrop-blur-sm dark:bg-card/80 rounded-xl">
        <CardContent className="p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-brand-primary to-purple-500 bg-clip-text text-transparent">
              Set new password
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your new password below
            </p>
          </div>

          {/* Error alert */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('confirm')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm && (
                <p className="text-xs text-destructive">{errors.confirm.message}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="h-10 w-full rounded-md bg-gradient-to-r from-brand-primary to-purple-600 text-white shadow-md hover:shadow-lg hover:opacity-90 transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset password'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
